/**
 * The door to a live, as the confirmed prototype draws it (g-front, "the door: camera and mic, then in"): when, the
 * talk and the host, where the live stands (starting soon with who is here, or live now), your own camera with the
 * microphone and camera as round buttons and the devices as quiet pills under it, and one button in as
 * yourself, with "Not you?" for somebody else on this browser.
 *
 * The camera and the microphone are the meeting's own (`Meeting.openDoor`), so what is chosen here carries into the
 * room. Nothing here opens the room's socket: someone still at the door is not counted as having come.
 */
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { PresenceFace } from "../../../shared/live-protocol";
import type { PublicBrand, PublicRegisterResponse } from "../../../shared/types";
import { canChooseSpeaker, type Meeting, type MeetingState } from "../../live/meeting/meeting";
import { deviceName, micSilent, otherMics } from "../../live/meeting/checks";
import { namedCameras } from "../../live/meeting/facing";
import { initialsOf } from "../../live/meeting/table";
import { fmt } from "../../lib/when";
import { Brand } from "../room/chrome";
import { GIcon, type GIconName } from "../room/GIcon";
import { DoorBackdrop } from "./DoorBackdrop";
import { JoinForm } from "./JoinForm";

interface DoorProps {
  brand: PublicBrand;
  title: string;
  /** "Tonight, Tue Nov 3 · 6:00 PM ET". */
  when: string;
  /** "Session 10 with", before the host's name. */
  withWords: string;
  hostName: string;
  hostAvatar: string | null;
  hostFirst: string;
  live: boolean;
  /** "Starts in 3 min", before the start. */
  startsIn: string | null;
  here: number;
  /** The room is waiting for a host who stepped out. */
  hostAway: boolean;
  faces: PresenceFace[];
  viewer: { first: string; avatar: string | null };
  /** Back at the door after leaving, or after the connection dropped. */
  back: "left" | "dropped" | null;
  joining: boolean;
  meeting: Meeting;
  state: MeetingState;
  onJoin: () => void;
  slug: string;
  siteKey: string | null;
  onSomeoneElse: (res: PublicRegisterResponse) => void;
  yourPage: string;
  poster: string | null;
  /** The host's door: the checks and Go live where a guest has Join. */
  host?: HostDoor;
}

export interface HostDoor {
  button: string;
  /** The button's words while the live starts. */
  going: string;
  disabled: boolean;
  count: number | null;
  checks: { name: string; line: string; status: "ok" | "wait" | "fix" | "note" }[];
  troubles: string[];
  backWords: string;
  /** "12 registered, 3 waiting", opening who is coming. */
  coming: ReactNode;
}

function RoundButton({ icon, off, label, onClick }: { icon: GIconName; off?: boolean; label: string; onClick: () => void }): ReactNode {
  return (
    <button className={["rb", off ? "off" : ""].filter(Boolean).join(" ")} type="button" aria-label={label} title={label} onClick={onClick}>
      <GIcon name={icon} size={22} />
    </button>
  );
}

function DevicePill({ icon, label, choices, chosen, onPick }: { icon: GIconName; label: string; choices: { id: string; label: string }[]; chosen: string; onPick: (id: string) => void }): ReactNode {
  if (choices.length === 0) return null;
  return (
    <label className="dp">
      <GIcon name={icon} size={16} />
      <select aria-label={label} value={chosen} onChange={(e) => onPick(e.target.value)}>
        {choices.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function LiveDoor(p: DoorProps): ReactNode {
  const { meeting, state } = p;
  const preview = useRef<HTMLVideoElement>(null);
  const track = meeting.previewTrack();
  useEffect(() => {
    const el = preview.current;
    if (!el) return;
    el.srcObject = track ? new MediaStream([track]) : null;
    if (track) void el.play().catch(() => {});
  }, [track]);

  // "Not now" while the browser asks: both stay off once it answers, and the door says they can still take part.
  const [notNow, setNotNow] = useState(false);
  const quieted = useRef(false);
  useEffect(() => {
    if (!notNow || quieted.current || state.media !== "ready") return;
    quieted.current = true;
    if (state.mine.micOn) void meeting.toggleMic();
    if (state.mine.cameraOn) void meeting.toggleCamera();
  }, [notNow, state.media, state.mine.micOn, state.mine.cameraOn, meeting]);
  const [askGone, setAskGone] = useState(false);
  useEffect(() => {
    if (!notNow) return;
    const t = window.setTimeout(() => setAskGone(true), 2200);
    return () => window.clearTimeout(t);
  }, [notNow]);
  const asking = state.media === "asking" && !askGone;
  const refused = state.media === "refused" && !askGone;

  const [someoneElse, setSomeoneElse] = useState(false);
  const { micOn, cameraOn } = state.mine;
  const hd = p.host;
  const joinWord = hd ? (p.joining ? hd.going : hd.button) : p.back ? "Join again" : `Join as ${p.viewer.first}`;
  const join = (wide: boolean) => (
    <button className={wide ? "btn btn-p btn-w" : "btn btn-p"} type="button" disabled={hd ? hd.disabled || p.joining : p.joining} aria-busy={p.joining || undefined} onClick={p.onJoin}>
      {p.joining ? <span className="spin" /> : <GIcon name={hd ? "stage" : "door"} size={20} />} {joinWord}
    </button>
  );

  return (
    <div className="door">
      <DoorBackdrop poster={p.poster} avatar={p.hostAvatar} />
      <div className="door-shade" />
      <header className="top">
        <Brand brand={p.brand} />
        <div className="top-r">
          <a className="top-link" href={p.yourPage}>
            <GIcon name="home" size={16} /> {hd ? hd.backWords : "Your page"}
          </a>
        </div>
      </header>
      <div className="door-wrap">
        <div className="door-r door-head">
          <p className="eyebrow">{p.when}</p>
          <h1 className="h-1">{p.title}</h1>
          <div className="host">
            {p.hostAvatar ? <img className="av" src={p.hostAvatar} alt="" /> : <span className="av av-i">{initialsOf(p.hostName)}</span>}
            <span>
              {p.withWords} <b>{p.hostName}</b>
            </span>
          </div>
          <div className="door-st">
            {p.live ? (
              <>
                <span className="chip">
                  <span className="live-dot" />
                  Live now
                </span>
                {p.here > 0 ? <span className="muted">{fmt(p.here)} here</span> : null}
              </>
            ) : (
              <>
                {p.startsIn ? (
                  <span className="chip soon">
                    <span className="live-dot" style={{ background: "var(--brass)", animation: "none" }} />
                    {p.startsIn}
                  </span>
                ) : null}
                {p.faces.length > 0 ? (
                  <span className="stack">
                    {p.faces.slice(-3).map((f) =>
                      f.photo ? (
                        <img key={f.key} src={f.photo} alt="" />
                      ) : (
                        <span key={f.key} className="av av-i">
                          {f.name.charAt(0)}
                        </span>
                      ),
                    )}
                  </span>
                ) : null}
                {p.here > 0 ? <span className="muted">{fmt(p.here)} here</span> : null}
              </>
            )}
          </div>
          {hd ? (
            <>
              <div className="hk-checks">
                {hd.checks.map((c) => (
                  <p key={c.name} className={`hk-check ${c.status}`}>
                    <GIcon name={c.status === "ok" ? "check" : c.status === "wait" ? "clock" : "wifi"} size={16} />
                    <span>
                      <b>{c.name}</b> {c.line}
                    </span>
                  </p>
                ))}
              </div>
              {hd.troubles.map((t) => (
                <p key={t} className="door-left bad" role="status">
                  {t}
                </p>
              ))}
              {hd.coming}
            </>
          ) : null}
          {p.back === "dropped" ? (
            <p className="door-left bad" role="alert">
              <GIcon name="wifi" size={18} /> The connection to the live dropped. Join again.
            </p>
          ) : p.back === "left" ? (
            <p className="door-left">
              You left the live.{" "}
              {p.live && p.hostAway
                ? `${p.hostFirst} stepped out, and your seat is still here.`
                : `${p.live ? `${p.hostFirst} is still on, and your seat` : "Your seat"} is still here.`}
            </p>
          ) : null}
          {state.failure && p.back !== "dropped" ? (
            <p className="door-left bad" role="alert">
              {state.failure}
            </p>
          ) : null}
        </div>
        <div className="door-l">
          <div className={["prev", cameraOn ? "" : "off"].filter(Boolean).join(" ")}>
            <video ref={preview} className="me-cam" muted playsInline aria-label="Your camera" />
            <div className="prev-off">
              {p.viewer.avatar ? <img className="av" src={p.viewer.avatar} alt="" /> : <span className="av av-i">{p.viewer.first.charAt(0)}</span>}
              <span>Your camera is off</span>
            </div>
            {micOn ? (
              <span className="prev-tag" aria-label="We can hear you">
                <span className="meter">
                  <i />
                  <i />
                  <i />
                  <i />
                </span>
              </span>
            ) : null}
            <div className="prev-ctl">
              <RoundButton icon={micOn ? "mic" : "micoff"} off={!micOn} label={micOn ? "Turn your microphone off" : "Turn your microphone on"} onClick={() => void meeting.toggleMic()} />
              <RoundButton icon={cameraOn ? "cam" : "camoff"} off={!cameraOn} label={cameraOn ? "Turn your camera off" : "Turn your camera on"} onClick={() => void meeting.toggleCamera()} />
            </div>
            {hd?.count != null ? (
              <div className="hk-count321" aria-live="assertive">
                <b key={hd.count}>{hd.count}</b>
                <span>Going live</span>
              </div>
            ) : null}
            {asking ? (
              <div className="ask">
                {notNow ? (
                  <b>You can still watch, chat and react</b>
                ) : (
                  <>
                    <span className="arrow">
                      <GIcon name="chu" size={16} /> In the address bar
                    </span>
                    <b>Allow your camera and microphone</b>
                    <button className="btn-q" type="button" onClick={() => setNotNow(true)}>
                      Not now
                    </button>
                  </>
                )}
              </div>
            ) : refused ? (
              <div className="ask">
                <b>You can still watch, chat and react</b>
              </div>
            ) : null}
          </div>
          <div className="dev-p">
            <DevicePill icon="cam" label="Camera" choices={namedCameras(state.cameras)} chosen={state.cameraId} onPick={(id) => void meeting.chooseCamera(id)} />
            <DevicePill icon="mic" label="Microphone" choices={state.microphones} chosen={state.micId} onPick={(id) => void meeting.chooseMic(id)} />
            {canChooseSpeaker() ? <DevicePill icon="vol" label="Speaker" choices={state.speakers} chosen={state.speakerId} onPick={(id) => meeting.chooseSpeaker(id)} /> : null}
          </div>
          {micSilent(state.readings.mic, state.microphones.length - 1) ? (
            <div className="mic-silent" role="status">
              <p>This microphone is silent. Pick another:</p>
              <div>
                {otherMics(state.microphones, state.micId).map((m) => (
                  <button key={m.id} className="btn btn-g" type="button" onClick={() => void meeting.chooseMic(m.id)}>
                    {deviceName(m.label)}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <div className="door-r door-act">
          {hd ? (
            <div>
              <div className="door-go">{join(false)}</div>
            </div>
          ) : someoneElse ? (
            <JoinForm
              slug={p.slug}
              siteKey={p.siteKey}
              className="door-who"
              button="Join now"
              note={`${p.hostFirst} will know you came. Use the email you signed up with.`}
              onIn={p.onSomeoneElse}
            />
          ) : (
            <div>
              <div className="door-go">{join(false)}</div>
              <div className="door-x">
                <button className="btn-q" type="button" onClick={() => setSomeoneElse(true)}>
                  Not you?
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {someoneElse && !hd ? null : <div className="door-stick">{join(true)}</div>}
    </div>
  );
}
